import { useCallback, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { FullScreenDialog } from '@/components/ui/full-screen-dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import OrganizationChart from 'components/IncidentConfig/OrganizationChart';
import { RosterDraftProvider } from 'components/IncidentConfig/CreateIncident/CreateRosterDraftContext';
import RosterActions from 'components/IncidentConfig/roster/RosterActions';
import type { DraftRosterAssignment } from 'components/IncidentConfig/CreateIncident/createIncidentModel';
import {
  useCreateRosterTemplate,
  useUpdateRosterTemplate,
  type RosterTemplateLibraryEntry,
} from 'hooks/rosterTemplateLibrary/rosterTemplateLibraryHooks';
import { selectGroupGuid } from 'slices/commonSelectors';
import {
  duplicateSeatNames,
  nodesFromSeats,
  seatsFromNodes,
  type TemplateEditorSeed,
} from './rosterTemplateSeats';

const NO_ASSIGNMENTS: DraftRosterAssignment[] = [];

export function RosterTemplateEditorDialog({
  existing,
  seed,
  onClose,
}: {
  existing: RosterTemplateLibraryEntry | null;
  seed: TemplateEditorSeed;
  onClose: () => void;
}) {
  const groupGuid = useSelector(selectGroupGuid);
  const createTemplate = useCreateRosterTemplate();
  const updateTemplate = useUpdateRosterTemplate();

  const [name, setName] = useState(seed.name);
  const [description, setDescription] = useState(seed.description ?? '');
  const [seats, setSeats] = useState(() => seatsFromNodes(seed.nodes));

  const handleChange = useCallback((nextSeats: typeof seats) => {
    setSeats(nextSeats);
  }, []);

  const duplicates = useMemo(() => duplicateSeatNames(seats), [seats]);
  const hasBlankSeat = seats.some((seat) => !seat.name.trim());
  const nextNodes = useMemo(() => nodesFromSeats(seats), [seats]);
  const isDirty =
    name.trim() !== seed.name.trim() ||
    (description.trim() || null) !== (seed.description ?? null) ||
    JSON.stringify(nextNodes) !== JSON.stringify(seed.nodes);

  const saving = createTemplate.isPending || updateTemplate.isPending;
  const canSave =
    !!name.trim() &&
    !hasBlankSeat &&
    duplicates.length === 0 &&
    (existing ? isDirty : true) &&
    !saving;

  const handleSave = () => {
    const payload = {
      name: name.trim(),
      description: description.trim() || null,
      attachOnly: existing ? existing.attachOnly : false,
      nodes: nextNodes,
    };
    if (existing) {
      updateTemplate.mutate(
        { id: existing.id, ...payload },
        { onSuccess: onClose }
      );
      return;
    }
    createTemplate.mutate(payload, { onSuccess: onClose });
  };

  if (!groupGuid) return null;

  return (
    <RosterDraftProvider
      groupGuid={groupGuid}
      seats={seats}
      assignments={NO_ASSIGNMENTS}
      creator={null}
      structureOnly
      onChange={handleChange}
    >
      <FullScreenDialog
        open
        onOpenChange={(next) => {
          if (!next) onClose();
        }}
        title={existing ? `Edit ${existing.name}` : 'New roster template'}
        description="Build the chart the same way you would on an incident. People and resources are not saved."
        bodyClassName="tw:!flex tw:!min-h-0 tw:!flex-col tw:!overflow-hidden tw:!p-3"
        headerLeading={
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={onClose}
            disabled={saving}
          >
            Cancel
          </Button>
        }
        header={
          <div className="tw:grid tw:gap-3 tw:md:grid-cols-[minmax(0,1fr)_minmax(0,1.4fr)] tw:md:items-end">
            <div className="tw:flex tw:flex-col tw:gap-1">
              <Label htmlFor="roster-template-editor-name">Name</Label>
              <Input
                id="roster-template-editor-name"
                value={name}
                maxLength={200}
                onChange={(event) => setName(event.target.value)}
              />
            </div>
            <div className="tw:flex tw:flex-col tw:gap-1">
              <Label htmlFor="roster-template-editor-description">
                Description
              </Label>
              <Textarea
                id="roster-template-editor-description"
                value={description}
                rows={1}
                className="tw:min-h-9 tw:resize-none"
                onChange={(event) => setDescription(event.target.value)}
              />
            </div>
          </div>
        }
        footer={
          <>
            {duplicates.length > 0 ? (
              <p className="tw:mr-auto tw:m-0 tw:text-xs tw:text-destructive">
                Seat names must be unique. Rename: {duplicates.join(', ')}
              </p>
            ) : hasBlankSeat ? (
              <p className="tw:mr-auto tw:m-0 tw:text-xs tw:text-destructive">
                Every seat needs a name.
              </p>
            ) : null}
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={onClose}
              disabled={saving}
            >
              Cancel
            </Button>
            <Button
              type="button"
              size="sm"
              disabled={!canSave}
              onClick={handleSave}
            >
              {saving ? 'Saving…' : 'Save template'}
            </Button>
          </>
        }
      >
        <RosterActions hideAnalytics />
        <div className="tw:min-h-0 tw:flex-1 tw:overflow-auto">
          <OrganizationChart incidentMembers={[]} />
        </div>
      </FullScreenDialog>
    </RosterDraftProvider>
  );
}
